import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";

import apiRequest from "@/services/apiRequest";
import { normalizeSnackbarMessage } from "@/utilities/messageFormatter";

Vue.use(Vuex);

const getReducedState = (state) => ({
  user: state.user,
  permissions: state.permissions,
  roleName: state.roleName,
  selectedStore: state.selectedStore,
  workstationPages: state.workstationPages,
});

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: getReducedState,
});

const getDefaultState = () => {
  return {
    toastData: {},
    // Global unsaved-changes guard (used by router + App-level dialog)
    hasUnsavedChanges: false,
    unsavedChangesDialog: {
      open: false,
      pendingRoute: null,
    },
    user: null,
    users: [],
    permissions: [],
    roleName: "",
    selectedStore: {},
    sidebarCollapsed: true,
    workstationPages: [],
  };
};

const store = {
  state: getDefaultState(),

  getters: {
    getToastData: (state) => state.toastData,
    getHasUnsavedChanges: (state) => state.hasUnsavedChanges,
    getUnsavedChangesDialog: (state) => state.unsavedChangesDialog,
    getUser: (state) => state.user,
    getUsers: (state) => state.users,
    getUserById: (state) => (userId) =>
      state.users.find((user) => user.id === userId),
    isAuthenticated: (state) => !!state.user,
    hasPermission: (state) => (permission) => {
      // If user has admin permission or all permissions, return true
      if (state.roleName === "Admin") {
        return true;
      }
      return state.permissions.includes(permission);
    },
    userRole: (state) => state.roleName,

    checkUserHasPermission: (state) => (permission) => {
      const lowerCaseRoleName = state.roleName?.toLowerCase();
      const hasAdminPermission =
        lowerCaseRoleName === "admin" ||
        state.permissions?.includes("admin") ||
        state.permissions?.includes("all");

      if (hasAdminPermission) {
        return true;
      }

      // Check if user has the required permission
      return state.permissions?.includes(permission);
    },

    getSelectedStore: (state) => state.selectedStore,
    getLoggedInUserStore: (state, getters) => getters.getUser.stores || [],
    getWorkstationPages: (state) => state.workstationPages,
  },

  mutations: {
    setToastData(state, data) {
      state.toastData = data;
    },
    setHasUnsavedChanges(state, value) {
      state.hasUnsavedChanges = Boolean(value);
    },
    openUnsavedChangesDialog(state, pendingRoute) {
      state.unsavedChangesDialog = {
        open: true,
        pendingRoute: pendingRoute || null,
      };
    },
    closeUnsavedChangesDialog(state) {
      state.unsavedChangesDialog = {
        open: false,
        pendingRoute: null,
      };
    },
    setUser(state, user) {
      state.user = user;
    },
    setUsers(state, users) {
      state.users = users;
    },
    setPermissions(state, permissions) {
      state.permissions = permissions;
    },
    setRoleName(state, roleName) {
      state.roleName = roleName;
    },
    clearUserData(state) {
      state.user = null;
      state.users = [];
      state.permissions = [];
      state.roleName = "";
    },

    setSelectedStore(state, store) {
      state.selectedStore = store;
    },
    setSidebarCollapsed(state, collapsed) {
      state.sidebarCollapsed = collapsed;
    },
    setWorkstationPages(state, pages) {
      state.workstationPages = pages;
    },
  },

  actions: {
    handleSnackBar(context, response) {
      console.log("🎯 handleSnackBar called with:", response);

      let message = "Something went wrong";
      let color = "error";

      // Define possible message sources in order of priority
      const messageSources = [
        { path: response?.response?.data?.error_message, type: "error" },
        { path: response?.response?.data?.data?.error_message, type: "error" },
        { path: response?.response?.data?.message, type: "error" },
        { path: response?.response?.data?.data?.message, type: "error" },
        { path: response?.response?.data?.error, type: "error" },
        { path: response?.response?.data?.detail, type: "error" },
        { path: response?.response?.data?.errors, type: "error" },
        { path: response?.response?.data?.validation_errors, type: "error" },
        // Handle array of errors
        {
          path: Array.isArray(response?.response?.data?.errors)
            ? response?.response?.data?.errors.join(", ")
            : null,
          type: "error",
        },
        // Handle validation errors object
        {
          path: response?.response?.data?.validation_errors
            ? Object.values(response?.response?.data?.validation_errors)
                .flat()
                .join(", ")
            : null,
          type: "error",
        },
        // Handle status text for generic errors
        {
          path:
            response?.response?.statusText && response?.response?.status >= 400
              ? `${response?.response?.status}: ${response?.response?.statusText}`
              : null,
          type: "error",
        },
        // Generic Axios error message should be last priority
        { path: response?.message, type: "error" },
      ];

      // Find the first available message
      for (const source of messageSources) {
        if (source.path) {
          message = source.path;
          color = source.type;
          console.log(
            "✅ Found error message:",
            message,
            "from source:",
            source
          );
          break;
        }
      }

      // Clean up the message if it's too long
      if (message && message.length > 200) {
        message = message.substring(0, 200) + "...";
      }

      // Normalize message to sentence case for consistency
      message = normalizeSnackbarMessage(message);

      console.log("🎉 Final error message:", message);

      context.commit("setToastData", {
        message,
        color,
        snackbar: true,
      });
    },
    async fetchUsers({ state, commit, dispatch }, { force = false } = {}) {
      if (!force && state.users.length) {
        return state.users;
      }

      try {
        const response = await dispatch("getBackofficeData", {
          url: "users",
        });

        if (response?.data?.status) {
          const users = response.data.data || [];
          commit("setUsers", users);
          return users;
        }

        commit("setUsers", []);
        return [];
      } catch (error) {
        console.error("Error fetching users list:", error);
        throw error;
      }
    },

    // Backoffice Functions Start
    getBackofficeData(context, { url, headerToken }) {
      return new Promise((resolve, reject) => {
        let done = async (response) => {
          if (response.status == 200 || response.status == 202) {
            resolve(response);
          } else {
            // Error handling is now done automatically in apiRequest.js
            reject(response);
          }
        };
        apiRequest.backofficeGet(url, done, headerToken);
      });
    },

    postBackofficeData(context, payload) {
      return new Promise((resolve, reject) => {
        let done = async (response) => {
          if (response.status == 200 || response.status == 201) {
            resolve(response);
          } else {
            // Error handling is now done automatically in apiRequest.js
            reject(response);
          }
        };
        apiRequest.backofficePost(
          payload.url,
          payload.data,
          done,
          payload.headerToken
        );
      });
    },

    putBackofficeData(context, payload) {
      return new Promise((resolve, reject) => {
        let done = async (response) => {
          if (response.status == 200 || response.status == 202) {
            resolve(response);
          } else {
            // Error handling is now done automatically in apiRequest.js
            reject(response);
          }
        };
        apiRequest.backofficePut(
          payload.url,
          payload.data,
          done,
          payload.headerToken
        );
      });
    },

    deleteBackofficeData(context, { url, headerToken }) {
      return new Promise((resolve, reject) => {
        let done = async (response) => {
          if (response.status == 200 || response.status == 204) {
            resolve(response);
          } else {
            // Error handling is now done automatically in apiRequest.js
            reject(response);
          }
        };
        apiRequest.backofficeDelete(url, done, headerToken);
      });
    },

    patchBackofficeData(context, payload) {
      return new Promise((resolve, reject) => {
        let done = async (response) => {
          if (response.status == 200 || response.status == 202) {
            resolve(response);
          } else {
            // Error handling is now done automatically in apiRequest.js
            reject(response);
          }
        };
        apiRequest.backofficePatch(
          payload.url,
          payload.data,
          done,
          payload.headerToken
        );
      });
    },
    // Backoffice Functions End

    login({ commit }, userData) {
      // Set user data
      commit("setUser", userData);

      // Set permissions if available
      if (userData.permission_names) {
        commit("setPermissions", userData.permission_names);
      }

      // Set role name if available
      if (userData.role_name) {
        commit("setRoleName", userData.role_name);
      }
    },

    logout({ commit }) {
      commit("clearUserData");
    },

    clearVuexStore({ state }) {
      Object.assign(state, getDefaultState());
      //clear local storage
      localStorage.clear();
    },
  },

  plugins: [vuexLocal.plugin],
  modules: {},
};

export default new Vuex.Store(store);
